import React, { useEffect, useRef } from 'react';
import { Brain, Search, Check, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const services = [
  {
    icon: <Brain className="h-10 w-10 text-primary-300" />,
    title: "AI Consulting",
    tagline: "Strategy-first AI adoption.",
    description: "We embed ourselves in your business, audit your workflows, map your customer journey, and build a prioritised AI roadmap that actually gets implemented.",
    benefits: [
      "Workflow & process audit",
      "AI opportunity identification",
      "Implementation roadmap",
      "ROI-first prioritisation",
      "Ongoing implementation support",
    ],
    link: "/ai-consultation",
    cta: "Learn More",
  },
  {
    icon: <Search className="h-10 w-10 text-primary-300" />,
    title: "Generative Engine Optimisation (GEO)",
    tagline: "Get found in AI-generated answers.",
    description: "When people ask ChatGPT, Gemini, or Perplexity about your industry, we make sure your business is the one they recommend. GEO is the next frontier of search.",
    benefits: [
      "AI visibility audit across all platforms",
      "AI-optimised content creation",
      "Technical schema & structured data",
      "Authority & citation building",
      "Monthly GEO reporting",
    ],
    link: "/geo",
    cta: "Learn More",
  },
];

const Services: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleIntersect = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const cards = entry.target.querySelectorAll('.service-card');
          cards.forEach((card, index) => {
            (card as HTMLElement).style.transitionDelay = `${index * 150}ms`;
            card.classList.remove('opacity-0', 'translate-y-8');
            card.classList.add('opacity-100', 'translate-y-0');
          });
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersect, {
      threshold: 0.1,
      rootMargin: '50px',
    });

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => { if (sectionRef.current) observer.unobserve(sectionRef.current); };
  }, []);

  return (
    <section id="services" className="section bg-black pb-32 md:pb-24" ref={sectionRef}>
      <div className="container">
        <div className="section-title mb-16">
          <h2 className="mb-4 text-white">What We Do</h2>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Two focused services — built for businesses that want to lead in the age of AI.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {services.map((service, index) => (
            <div
              key={index}
              className="service-card opacity-0 translate-y-8 transition-all duration-700 group bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 hover:border-white/20 rounded-2xl p-10 flex flex-col"
            >
              <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center mb-6">
                {service.icon}
              </div>
              <h3 className="text-2xl font-bold text-white mb-1">{service.title}</h3>
              <p className="text-primary-300 text-sm font-medium mb-4">{service.tagline}</p>
              <p className="text-gray-400 leading-relaxed mb-6">{service.description}</p>
              <ul className="space-y-2.5 mb-8 flex-1">
                {service.benefits.map((b, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-sm text-gray-300">
                    <Check className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => { navigate(service.link); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="inline-flex items-center gap-2 text-white font-medium text-sm group-hover:gap-3 transition-all"
              >
                {service.cta}
                <ChevronRight className="h-4 w-4 text-primary-300" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
